import { useState, useEffect } from "react";
import type { EChartsOption } from "echarts";
import BaseChart from "../../baseChart";
import { numFormatter } from "../../../helpers/utils";
import { chartIcons, createDataZoomSlider } from "../../baseChart/utils";
import { convertTimestampToTimeZone } from "./utils";

// Helper function for number formatting
const formatNumber = (num: number) => {
  return typeof num === "string"
    ? parseFloat(num).toLocaleString("en-US")
    : num.toLocaleString("en-US");
};

export type MultiSeriesComboChartConfiguration = {
  config: {
    mark_lines: {
      name: string;
      axis: "y-axis" | "x-axis";
      value: number | string;
      value_formatter: string;
      color?: string;
    }[];
  };
  data: {
    series: {
      name: string;
      type: "bar" | "line";
      value_formatter: string;
      data: {
        category: string;
        value: number;
        offset?: string;
      }[];
    }[];
  };
};

interface IMultiSeriesComboChartProps {
  width: number;
  height: number;
  data: MultiSeriesComboChartConfiguration;
  extraConfigs?: any;
  limitBars?: number;
}

const MultiSeriesComboChart = ({
  data,
  width,
  height,
  extraConfigs,
  limitBars = 40,
}: IMultiSeriesComboChartProps) => {
  const [option, setOption] = useState<EChartsOption>({});
  const [firstElementLength, setFirstElementLength] = useState(0);

  const generateChartSeriesData = (chartData: MultiSeriesComboChartConfiguration) => {
    const barSeriesData = chartData.data.series.filter((d) => d.type === "bar");
    const lineSeriesData = chartData.data.series.filter((d) => d.type === "line");
    
    const seriesData = chartData.data.series.map((d) => ({
      type: d.type,
      name: d.name,
      markLine: {},
      yAxisIndex: barSeriesData.length > 0 ? (d.type === "bar" ? 0 : 1) : 0,
      tooltip: {
        show: true,
        valueFormatter: (value: any) =>
          `${formatNumber(value)} ${d.value_formatter}`,
      },
      // Preserve exact order from data - no sorting applied
      data: d.data.map((i) => ({
        name: i.category,
        value: i.value,
      })),
    }));

    // Add mark lines to first series if available
    if (seriesData.length > 0) {
      seriesData[0].markLine = chartData.config?.mark_lines
        ? {
            symbolSize: 0,
            data: chartData.config.mark_lines.map((d) => ({
              name: d.name,
              [d.axis === "y-axis" ? "yAxis" : "xAxis"]: d.value,
              lineStyle: {
                color: d.color ?? "red",
                width: 3,
              },
              label: {
                show: true,
                formatter: ({ name, value }: { name: any; value: any }) =>
                  `${name}: ${formatNumber(value)} ${d.value_formatter}`,
                position: "insideMiddle",
                backgroundColor: d.color ?? "red",
                color: "#ababab",
                padding: 3,
              },
            })),
          }
        : {};
    }

    return seriesData;
  };

  const generateYAxesScales = (chartData: MultiSeriesComboChartConfiguration) => {
    const barSeriesData = chartData.data.series.filter((d) => d.type === "bar");
    const lineSeriesData = chartData.data.series.filter((d) => d.type === "line");
    const axesScales: any[] = [];

    if (barSeriesData.length > 0) {
      axesScales.push({
        type: "value",
        name: barSeriesData
          .map((d) => d.name)
          .reduce((a, b) => a + " / " + b),
        nameLocation: "end",
        nameTextStyle: {
          align: "left",
        },
        axisLabel: {
          formatter: (value: any) =>
            `${numFormatter(value)} ${barSeriesData[0].value_formatter}`,
        },
      });
    }

    if (lineSeriesData.length > 0) {
      axesScales.push({
        type: "value",
        name:
          barSeriesData.length > 0 && lineSeriesData.length < 3
            ? lineSeriesData
                .map((d) => d.name)
                .reduce((a, b) => a + " / " + b)
            : "",
        nameLocation: "end",
        nameTextStyle: {
          align: "right",
        },
        axisLabel: {
          formatter: (value: any) =>
            `${numFormatter(value)} ${lineSeriesData[0].value_formatter}`,
        },
      });
    }

    return axesScales;
  };

  const generateXAxisData = (chartData: MultiSeriesComboChartConfiguration) => {
    // Preserve exact order from the first series data - no sorting
    const categories = chartData.data.series[0].data.map((d) => d.category);
    
    // Handle hourly trends special case
    if (extraConfigs && "trendPeriod" in extraConfigs) {
      if (
        extraConfigs.trendPeriod &&
        extraConfigs.trendPeriod.includes("trends-hourly")
      ) {
        return chartData.data.series[0].data.map((d) => {
          if (d.offset) {
            return convertTimestampToTimeZone(
              d.category,
              d.offset,
              "dd/MM - hh:mm a"
            );
          }
          return d.category;
        });
      }
    }

    return categories;
  };

  useEffect(() => {
    if (!data || !data.data || !data.data.series || data.data.series.length === 0) {
      setOption({});
      setFirstElementLength(0);
      return;
    }

    const currentFirstElementLength = data.data.series[0].data.length;
    setFirstElementLength(currentFirstElementLength);

    const baseOption: EChartsOption = {
      tooltip: {
        trigger: "axis",
        axisPointer: {
          show: true,
          type: "shadow",
        },
      },
      legend: {
        top: "top",
        data: data.data.series.map((d) => d.name),
        type: "scroll",
      },
      grid: {
        left: "0px",
        right: "0px",
        containLabel: true,
        bottom: "30px",
      },
      xAxis: [
        {
          type: "category",
          data: generateXAxisData(data),
        },
      ],
      yAxis: generateYAxesScales(data),
      dataZoom:
        currentFirstElementLength > 1 ? createDataZoomSlider({ limitBars }) : [],
      series: generateChartSeriesData(data) as any,
    };

    setOption(baseOption);
  }, [data, extraConfigs, limitBars]);

  return (
    <BaseChart
      height={height}
      width={width}
      options={option}
      chartType="bar-line"
    />
  );
};

export default MultiSeriesComboChart;
